import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  PositionsEntityKind,
} from '@revelio/data-access';

import { applyGeoFilter } from '../utils/applyGeoFilter';
import type { AdjacentRole } from './useAdjacentRoles';
import type {
  GeoLevel,
  ResolvedProfileIds,
  RoleGranularity,
} from './useResolveProfileIds';

export interface SalaryTrendPoint {
  month: string;
  salary: number;
}

export interface RoleSalaryTrend {
  roleId: number;
  roleName: string;
  isCurrentRole: boolean;
  trend: SalaryTrendPoint[];
  /** Latest median salary */
  latestSalary: number | null;
}

interface UseRoleSalaryTrendsResult {
  trends: RoleSalaryTrend[];
  isLoading: boolean;
  error: string | null;
}

const MAX_ADJACENT_FOR_TREND = 5; // query up to 5 adjacent roles (plus current)
const TREND_MONTHS = 12; // Show last 12 data points

const ROLE_GRANULARITY_TO_PAY_ENTITY: Record<
  RoleGranularity,
  PositionsEntityKind
> = {
  role_k15000: PositionsEntityKind.RoleK15000,
  role_k1500: PositionsEntityKind.RoleK1500,
  role_k150: PositionsEntityKind.RoleK150,
  role_k7: PositionsEntityKind.RoleK7,
};

export const useRoleSalaryTrends = (
  adjacentRoles: AdjacentRole[],
  resolvedIds: ResolvedProfileIds,
  selectedGeo: GeoLevel | null,
  roleGranularity: RoleGranularity = 'role_k150',
  /** When set, guarantees this role is included in the query even if it's
   *  outside the top-N adjacent roles. */
  selectedRoleId: number | null = null
): UseRoleSalaryTrendsResult => {
  const entityType =
    ROLE_GRANULARITY_TO_PAY_ENTITY[roleGranularity] ??
    PositionsEntityKind.RoleK150;

  // Build entities: current role + adjacent roles, ensuring selectedRoleId is included
  const variables = useMemo(() => {
    if (!resolvedIds.roleId) {
      return undefined;
    }

    const entities: Array<{ EntID: number; EntType: PositionsEntityKind }> = [];

    // Current role (use k150 for current since that's the primary resolved level)
    entities.push({
      EntID: resolvedIds.roleId,
      EntType: PositionsEntityKind.RoleK150,
    });

    // Start with top adjacent roles
    const topAdjacent = adjacentRoles.slice(0, MAX_ADJACENT_FOR_TREND);
    const includedIds = new Set<number>([resolvedIds.roleId]);

    for (const role of topAdjacent) {
      if (!includedIds.has(role.id)) {
        entities.push({
          EntID: role.id,
          EntType: entityType,
        });
        includedIds.add(role.id);
      }
    }

    // Ensure selected role is always included
    if (selectedRoleId !== null && !includedIds.has(selectedRoleId)) {
      entities.push({
        EntID: selectedRoleId,
        EntType: entityType,
      });
    }

    const filters: CompositionV2Filters = { raw: false };
    if (selectedGeo) {
      applyGeoFilter(filters, selectedGeo);
    }

    return {
      req: {
        entities,
        filters,
      },
    };
  }, [
    resolvedIds.roleId,
    adjacentRoles,
    selectedGeo,
    entityType,
    selectedRoleId,
  ]);

  const [{ data, fetching, error }] = useQuery({
    query: PAY_V2_QUERY,
    variables: variables ?? {
      req: { entities: [], filters: { raw: false } },
    },
    pause: !variables,
  });

  const trends = useMemo((): RoleSalaryTrend[] => {
    const payEntities = data?.pay_v2?.entities;
    const months: string[] = (data?.pay_v2?.refs?.months ?? []).filter(
      (m): m is string => m !== null && m !== undefined
    );

    if (!payEntities || months.length === 0) {
      return [];
    }

    return payEntities
      .filter((e): e is NonNullable<typeof e> => e !== null && e !== undefined)
      .map((entity) => {
        const id = entity.metadata?.id ?? 0;
        const name =
          entity.metadata?.long_name ||
          entity.metadata?.short_name ||
          'Unknown';
        const isCurrentRole = id === resolvedIds.roleId;

        const salaryTotal = entity.metrics?.overtime?.SalaryTotal ?? [];

        // Take last N months of data
        const startIdx = Math.max(0, salaryTotal.length - TREND_MONTHS);
        const trend: SalaryTrendPoint[] = [];
        for (let i = startIdx; i < salaryTotal.length; i++) {
          const salary = salaryTotal[i];
          const month = months[i];
          if (salary !== null && salary !== undefined && month) {
            trend.push({ month, salary });
          }
        }

        const latestSalary =
          trend.length > 0 ? (trend[trend.length - 1]?.salary ?? null) : null;

        return {
          roleId: id,
          roleName: name,
          isCurrentRole,
          trend,
          latestSalary,
        };
      })
      .filter((t) => t.trend.length > 0);
  }, [data, resolvedIds.roleId]);

  return {
    trends,
    isLoading: fetching,
    error: error ? 'Failed to load salary trends' : null,
  };
};
